import React from 'react';
import { Dialog, DialogPortal, DialogOverlay } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Eye, User } from 'lucide-react';
import DriverDashboard from './DriverDashboard';
import { Driver } from '@/contexts/AppContext';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

interface DriverViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Driver | null;
  adminName: string;
}

const DriverViewModal: React.FC<DriverViewModalProps> = ({
  isOpen,
  onClose,
  driver,
  adminName,
}) => {
  if (!driver) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] h-[95vh] max-w-none",
            "border border-border/40 bg-background shadow-2xl rounded-lg",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "flex flex-col overflow-hidden"
          )}
        >
          {/* Visually hidden title and description for accessibility */}
          <VisuallyHidden.Root asChild>
            <DialogPrimitive.Title>
              Driver View Emulator - Viewing as {driver.driver_name}
            </DialogPrimitive.Title>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root asChild>
            <DialogPrimitive.Description>
              Preview mode showing the driver dashboard as {driver.driver_name} would see it. Admin: {adminName}
            </DialogPrimitive.Description>
          </VisuallyHidden.Root>

          {/* Custom Header Banner */}
          <div className="flex-shrink-0 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 text-white shadow-lg">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <Eye className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm sm:text-base flex items-center gap-2">
                    <span>Driver View Emulator</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">PREVIEW MODE</span>
                  </p>
                  <p className="text-xs sm:text-sm opacity-90">
                    Viewing as <span className="font-bold">{driver.driver_name}</span> • Admin: {adminName}
                  </p>
                </div>
              </div>
              <Button
                onClick={onClose}
                variant="outline"
                size="sm"
                className="bg-white text-orange-700 border-white hover:bg-orange-100 hover:text-orange-800 font-semibold shadow-md"
              >
                <X className="w-4 h-4 mr-2" />
                Close View
              </Button>
            </div>
          </div>

          {/* Driver Info Summary */}
          <div className="flex-shrink-0 bg-slate-800 border-b border-slate-700 px-4 py-2">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-yellow-400" />
                <span className="text-slate-400">Driver:</span>
                <span className="text-white font-medium">{driver.driver_name}</span>
              </div>
              {driver.email && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white">{driver.email}</span>
                </div>
              )}
              {driver.phone && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Phone:</span>
                  <span className="text-white">{driver.phone}</span>
                </div>
              )}
              {driver.rideWiseId && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">RideWise ID:</span>
                  <span className="text-white">{driver.rideWiseId}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Admin Rights:</span>
                <span className={driver.isAdmin ? 'text-green-400' : 'text-slate-500'}>
                  {driver.isAdmin ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Scrollable Driver Dashboard Content */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-900 via-black to-gray-800">
            <DriverDashboard
              driverName={driver.driver_name}
              vehicleId={driver.vehicleId}
              onLogout={onClose}
              isImpersonating={true}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default DriverViewModal;
